import {useContext, useEffect, useState} from "react";
import {useMessages} from "i18n";
import {ToastContext} from "Toast";
import {useUser} from "UserProvider";
import {responseStatus, serverMessage} from "utils/ErrorUtils";
import {useKeyedSingleFlight} from "utils/useSingleFlight";
import {canUseCustomDomain, planOf} from "home/gating";
import {AccountSubdomainApi, type SubdomainClaim} from "../AccountSubdomainApi";
import {accountApiFailureText} from "../accountApiFailure";
import {
	brandSuffixForHost,
	claimHostsForBrand,
	subdomainSectionState,
	validateLabel,
	type SubdomainSectionState,
} from "../subdomain/subdomainClaim";

/**
 * Contract of {@link useAccountSubdomain} — the thin React adapter over `SubdomainClaimService`
 * (kitecorp/transferify-api-service#350). Its domain rules live in plain account modules; this hook only
 * sequences requests and surfaces toasts (epic #26).
 */
export interface UseAccountSubdomain {
	/** Which of the editor's mutually exclusive states to render. */
	state: SubdomainSectionState;
	/** The claim, or null when nothing is claimed. */
	claim: SubdomainClaim | null;
	/**
	 * The claimed addresses to render, scoped to the brand the sender is browsing (ui#725). Not
	 * `claim.hosts`: the claim serves on every brand zone, but the editor advertises only the one whose
	 * site the sender is actually on. Empty when nothing is claimed.
	 */
	hosts: string[];
	/** The label currently in the input. */
	label: string;
	/** The suffix shown beside the input, for the brand the sender is on. */
	suffix: string;
	/** True while a request is in flight; the editor disables its controls. */
	busy: boolean;
	/** The error to show under the input, or null. */
	error: string | null;
	/** Whether the confirm dialog for releasing is open. */
	confirmOpen: boolean;
	setLabel: (value: string) => void;
	submit: () => Promise<void>;
	openConfirm: () => void;
	closeConfirm: () => void;
	release: () => Promise<void>;
}

export function useAccountSubdomain(): UseAccountSubdomain {
	const m = useMessages();
	const [user] = useUser();
	const {showErrorToast, showSuccessToast} = useContext(ToastContext);
	const [claim, setClaim] = useState<SubdomainClaim | null>(null);
	const [label, setLabel] = useState("");
	const [busy, setBusy] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const entitled = canUseCustomDomain(planOf(user));

	// Load once, on EVERY tier. This used to skip the request for a non-entitled sender as an optimisation,
	// which is what hid a downgraded owner's claim from them (ui#534): entitlement says nothing about whether
	// a claim exists, and a downgraded owner is indistinguishable from a never-entitled one until the api
	// answers — so there is no narrower condition than "always" that cannot hide a claim.
	//
	// It costs one GET, and only when the sender opens the sheet: `BrandSheet` is a MUI modal without
	// `keepMounted`, so this hook does not exist until then.
	useEffect(() => {
		let active = true;
		(async () => {
			try {
				const loaded = await AccountSubdomainApi.getClaim();
				if (active) {
					setClaim(loaded);
				}
			} catch (e) {
				if (active) {
					showErrorToast(failureText(e));
				}
			} finally {
				if (active) {
					setLoading(false);
				}
			}
		})();
		return () => {
			active = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Keys are per ACTION so claiming never blocks a release (ui#360).
	const flight = useKeyedSingleFlight();

	/**
	 * The api answers in the caller's language (`Accept-Language` rides every request), so its own sentence
	 * is what "reserved" / "taken" / "not on your plan" must read as — the catalog only supplies the two
	 * cases where there is no server sentence at all.
	 */
	const failureText = (e: unknown): string => accountApiFailureText(
		{
			status: responseStatus(e),
			message: serverMessage(e),
		},
		{network: m.account.subdomain.errors.network, generic: m.account.subdomain.errors.generic},
	);

	const submit = async () => {
		const verdict = validateLabel(label);
		if (!verdict.valid) {
			setError(m.account.subdomain.errors[verdict.error]);
			return;
		}
		await flight.run("claim", async () => {
			setBusy(true);
			setError(null);
			try {
				const claimed = await AccountSubdomainApi.claim(verdict.label);
				setClaim(claimed);
				if (claimed !== null) {
					setLabel("");
					showSuccessToast(m.account.subdomain.toasts.claimed);
				}
			} catch (e) {
				setError(failureText(e));
			} finally {
				setBusy(false);
			}
		});
	};

	const release = async () => {
		setConfirmOpen(false);
		await flight.run("release", async () => {
			setBusy(true);
			try {
				await AccountSubdomainApi.release();
				setClaim(null);
				setError(null);
				showSuccessToast(m.account.subdomain.toasts.released);
			} catch (e) {
				showErrorToast(failureText(e));
			} finally {
				setBusy(false);
			}
		});
	};

	// typeof guard: the sheet holding this editor is mounted by the NavBar on every route, including the
	// public ones vite-react-ssg prerenders, where there is no window.
	const hostname = typeof window === "undefined" ? null : window.location.hostname;

	return {
		state: subdomainSectionState({entitled, loading, hasClaim: claim !== null}),
		claim,
		hosts: claim === null ? [] : claimHostsForBrand(claim.hosts, hostname),
		label,
		suffix: brandSuffixForHost(hostname),
		busy,
		error,
		confirmOpen,
		setLabel,
		submit,
		openConfirm: () => setConfirmOpen(true),
		closeConfirm: () => setConfirmOpen(false),
		release,
	};
}
